/**
 * SkyView's scene: the timeline container's orthophoto, tiled model and point cloud, each shown or
 * hidden from the view settings, viewed with either a perspective or an orthographic camera. The
 * classic viewing mode shows one of them, see ViewSettings.showClassicView. In the CAD viewer, a
 * project without timeline data, it shows no timeline data, only the assets.
 */

import * as THREE from 'three';
import { Color } from 'three';

import { projectReferenceAdapter } from '../../react/state/project-reference-state/project-reference-adapter';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { CameraType } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { DxfUnit } from '../elements/dxf/types';
import { ReferencePlane } from '../elements/reference-plane/reference-plane';
import { Cesium3dTilesRenderer } from '../geodata/3d-tiled-models/cesium-3d-tiles/cesium-3d-tiles-renderer';
import { Tiled3DModelHandle } from '../geodata/3d-tiled-models/tiled-3d-model-handle';
import { Tiles3tzModel } from '../geodata/3d-tiled-models/tiles-3tz/tiles-3tz-model';
import { Tiled3dModel } from '../geodata/3d-tiled-models/type';
import {
  bindOrthophotoSettings,
  createActiveOrthoTileAsset,
} from '../geodata/ortho-tile-asset/active-ortho-tile-asset';
import {
  getOrthotileBounds,
  getOrthotileWgs84Bounds,
} from '../geodata/ortho-tile-asset/ortho-bounds';
import { OrthoTileAsset } from '../geodata/ortho-tile-asset/ortho-tile-asset';
import { Cesium3dTilesPointCloudRenderer } from '../geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-point-cloud-renderer';
import { EdlPass } from '../geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/edl-pass';
import { CesiumPointsMaterial } from '../geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { CopcPointCloud } from '../geodata/point-clouds/copc-point-cloud/copc-point-cloud';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
import { PointCloud } from '../geodata/point-clouds/types';
import { InputHandler } from '../input-handler';
import { LayerLoadingStatuses } from '../layer-loading-status';
import { applyLayerOpacity } from '../layer-opacity';
import { publish, subscribe, SubscriptionTopic, unsubscribe } from '../messaging/pubsub';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { renderOrders } from '../render-orders';
import { SceneManager } from '../scene-manager';
import { ScaleHandler } from '../service/dxf/scale-handler';
import { SunManager } from '../sun-manager';
import { getContainerOrigo, TimelineContainer } from '../timeline-manager';
import { disposeRecursive } from '../utilities/three/dispose';
import {
  ClassicSceneType,
  fromClassicSceneTransformation,
  resolveClassicView,
  toClassicSceneType,
  toClassicView,
} from '../view-scenes';
import { FlexibleCameraType, ViewLayer, ViewSettings } from '../view-settings';
import { WmsState } from '../wms-state';
import { getWebWorkerManager } from '../workers/web-worker-manager';
import { loadPointCloud, loadTiledModel } from './load-timeline-geodata';
import { Scene } from './scene';

const near = 0.2;
const far = 20000;
// Same depth as the ortho scene, seeing down to 1 km below MAS
const orthographicFar = 6000;
// The CAD viewer's drawings can be far from its origo, the coordinate system's center
const cadViewerFar = 100000;

/** How far, in metres, the timeline data may be from the scene's origo before it moves there. */
export const maxOrigoDistance = 2000;

/**
 * Project coords at scene (0,0,0), from the timeline container's metadata, so before its data has
 * loaded: the current origo while the container's data is near it, so a new timeline is shown
 * where the last one was; otherwise the tiled model's origo if it has one, then the point cloud's,
 * then the orthophoto's. A container without data keeps the current origo, so the scene stays
 * where it was; without a current one, the container with data closest in time gives it, then the
 * coordinate system's center.
 */
export function resolveSkyviewSceneOrigo(
  timelineContainer: TimelineContainer | undefined,
  current?: THREE.Vector3,
): THREE.Vector3 {
  const origo = timelineContainer && getContainerOrigo(timelineContainer);
  if (!origo) {
    return (
      current ??
      (timelineContainer &&
        ProjectGeodataManager.instance().resolveTimelineContainerOrigo(timelineContainer)) ??
      CoordinateManager.getInstance().getCoordinateSystemDefaultOrigo(
        projectReferenceAdapter.getCoordinateSystemOrDefault(),
      )
    );
  }
  return current && current.distanceTo(origo) <= maxOrigoDistance ? current : origo;
}

export type SkyviewSceneOptions = {
  defaultTimelineContainerId?: string;
  /** The classic view to show first, by the scene it was shown in, e.g. from a shared view. */
  defaultSceneType?: ClassicSceneType;
};

export class SkyviewScene extends Scene {
  private perspectiveCameraUuid: string;
  private orthographicCameraUuid: string;

  private orthoTileAsset: OrthoTileAsset | undefined;
  /**
   * The replaced orthophoto's overview, shown until the next timeline's orthophoto has loaded its
   * own, see keepOrthophotoOverview.
   */
  private orthophotoOverview: OrthoTileAsset | undefined;
  private hasTiledModel = false;
  private hasPointCloud = false;
  private edlPass?: EdlPass;

  private scaleHandler?: ScaleHandler;

  private unbindOrthophotoSettings: (() => void) | undefined;
  private unsubscribeViewSettings: (() => void) | undefined;
  private onToolChangedSubscription: string | undefined;
  /** Aborts the timeline container's load, see loadTimelineContainer. */
  private loadController: AbortController | undefined;
  /**
   * Aborts showing the data again, back from another scene, when the scene is hidden or another
   * timeline container is chosen, see activate.
   */
  private returnController: AbortController | undefined;
  /** The timeline container that is loading, or waiting to. */
  private loadingTimelineContainerId: string | undefined;
  /** Whether the camera is focused on the next timeline container to load, see open. */
  private focusOnLoad = false;

  /** Whether the scene has been opened, see open. */
  private opened = false;
  private active = false;
  /**
   * The timeline container chosen while the scene wasn't shown, or that hadn't loaded when it was
   * hidden, loaded once it is.
   */
  private pendingTimelineContainerId: string | undefined;

  constructor(private options: SkyviewSceneOptions = {}) {
    super();

    this.perspectiveCameraUuid = cameraManager().createPerspectiveCamera(60, 1, near, far);
    this.orthographicCameraUuid = cameraManager().createOrthographicCamera(
      window.innerWidth,
      window.innerHeight,
      1,
      orthographicFar,
    );
  }

  async init() {
    if (this.initialized) {
      return Promise.resolve();
    }

    this.initialized = true;
    getWebWorkerManager().init();
    ReferencePlane.instance.reset();
    CoordinateManager.getInstance().initialize();
    return Promise.resolve();
  }

  async activate() {
    console.log('Activated SkyView scene');
    this.active = true;
    const opening = !this.opened;
    if (opening) {
      this.open();
    }

    cameraManager().setAvailableCameras([this.perspectiveCameraUuid, this.orthographicCameraUuid]);
    this.setCameraDepth();

    if (ViewSettings.instance.isCadViewer) {
      this.activateCadViewer();
      this.followViewSettings();
      return;
    }

    // The comparison tool draws its own orthophotos
    this.onToolChangedSubscription = subscribe(SubscriptionTopic.SetTool, () =>
      this.applyLayerVisibility(),
    );

    // The timeline data loads once the scene is shown, each layer on its own
    const timelineContainerId = opening
      ? ProjectGeodataManager.instance().getActiveTimelineContainer()?.id
      : this.pendingTimelineContainerId;
    this.pendingTimelineContainerId = undefined;
    if (timelineContainerId) {
      this.activateCamera(ViewSettings.instance.camera, false);
      this.followViewSettings();
      void this.loadTimelineContainer(timelineContainerId, opening);
      return;
    }

    // Back from another scene, with the data it showed. Until the tiled model is activated, hiding
    // the scene stops it, and choosing another timeline container leaves the layers to that one.
    // The view settings are followed meanwhile, also by that container's layers.
    const returning = new AbortController();
    this.returnController = returning;
    this.showAvailableLayers();
    this.activateCamera(ViewSettings.instance.camera, false);
    this.followViewSettings();
    this.addPointCloud(PointCloudHandle.instance.activePointCloud);
    // Shown on its own, as the other layers load meanwhile
    this.addOrthophoto().catch((err) => console.error(err));
    try {
      // The model is the next return's if the scene is hidden and shown again meanwhile
      await this.addTiledModel(
        Tiled3DModelHandle.instance.getActiveTiledModel(),
        returning.signal,
        () => this.returnController === returning,
      );
    } catch (err) {
      if (!returning.signal.aborted) {
        throw err;
      }
    }
    if (returning.signal.aborted) {
      return;
    }
    this.applyLayerVisibility();
    cameraManager().focusOnScene();
    this.publishBoundingBox();
  }

  /**
   * Opens SkyView, before any timeline data has loaded: whether it is the CAD viewer, the
   * timeline container it opens with, the classic view, and the projection with the origo from
   * the container's metadata.
   */
  private open() {
    this.opened = true;
    // What the last SkyView showed is not shown again
    ViewSettings.instance.resetView();
    const geodata = ProjectGeodataManager.instance();
    const cadViewer = !geodata.hasTimelineGeodata();
    ViewSettings.instance.setCadViewer(cadViewer);
    SceneManager.instance.clearColor = new Color(cadViewer ? 0x000000 : 0xccddf0);

    if (this.options.defaultTimelineContainerId) {
      // The CAD viewer shows no timeline data, but the requested timeline is still the active one
      geodata.selectTimelineContainer(this.options.defaultTimelineContainerId);
    }

    if (cadViewer) {
      // The CAD viewer opens from above, also after a project viewed in perspective
      ViewSettings.instance.setCamera('orthographic');
    } else if (this.options.defaultSceneType && !ViewSettings.instance.isFlexible) {
      ViewSettings.instance.showClassicView(toClassicView(this.options.defaultSceneType));
    }

    const coordinateSystem = projectReferenceAdapter.getCoordinateSystemOrDefault();
    geodata.setGeodataState({
      projection: coordinateSystem?.proj4,
      projectionName: coordinateSystem?.name,
    });
    const projection = geodata.getProjectProjection();
    if (!projection) {
      throw new Error('Failed to open SkyView. The project projection is not defined.');
    }

    const origo = cadViewer
      ? CoordinateManager.getInstance().getCoordinateSystemDefaultOrigo(coordinateSystem)
      : resolveSkyviewSceneOrigo(geodata.getActiveTimelineContainer());
    CoordinateManager.getInstance().setProjectProjectionData(projection, origo);

    if (!cadViewer) {
      this.showAvailableLayers();
    }
  }

  /**
   * Shows the CAD viewer: no timeline data, the camera at the coordinate system's center, and
   * the scale of the drawings.
   */
  private activateCadViewer() {
    // Skips the camera transformation, so the camera stays at 0,0 in local coordinates
    this.activateCamera(ViewSettings.instance.camera, true);

    if (!this.scaleHandler) {
      this.scaleHandler = new ScaleHandler(DxfUnit.Meters);
    }
    this.scaleHandler.activate();
  }

  private followViewSettings() {
    this.unsubscribeViewSettings = ViewSettings.instance.subscribe(() => {
      this.activateCamera(ViewSettings.instance.camera, true);
      this.applyLayerVisibility();
    });
  }

  /** The CAD viewer sees further, see cadViewerFar. */
  private setCameraDepth() {
    const isCadViewer = ViewSettings.instance.isCadViewer;
    const depths: [string, number][] = [
      [this.perspectiveCameraUuid, isCadViewer ? cadViewerFar : far],
      [this.orthographicCameraUuid, isCadViewer ? cadViewerFar : orthographicFar],
    ];
    for (const [uuid, cameraFar] of depths) {
      const camera = cameraManager().getCamera(uuid)?.getTHREECamera();
      if (camera && camera.far !== cameraFar) {
        camera.far = cameraFar;
        camera.updateProjectionMatrix();
      }
    }
  }

  /**
   * Shows the timeline container's data in place of the shown one's, keeping the camera where it
   * is, and the origo while the data is near it. The orthophoto, tiled model and point cloud each
   * load on their own, see LayerLoadingStatuses. The shown orthophoto's overview stays until the
   * next one's has loaded, see keepOrthophotoOverview. Only the last container chosen is shown:
   * choosing another aborts the one loading without waiting for it, and what it still loads is
   * cleared. A container chosen while the scene isn't shown loads once it is.
   *
   * @param opening True for the container SkyView opens with, which the camera is then focused on.
   */
  loadTimelineContainer(timelineContainerId: string, opening = false) {
    if (opening) {
      this.focusOnLoad = true;
    }

    if (!this.active) {
      this.pendingTimelineContainerId = timelineContainerId;
      ProjectGeodataManager.instance().selectTimelineContainer(timelineContainerId);
      return Promise.resolve();
    }

    // The data shown again, back from another scene, is the last container's
    this.returnController?.abort();
    this.loadController?.abort();
    const controller = new AbortController();
    this.loadController = controller;
    this.loadingTimelineContainerId = timelineContainerId;

    return this.showTimelineContainer(timelineContainerId, controller.signal);
  }

  /**
   * Loads the timeline container in place of the shown one, see loadTimelineContainer. Once the
   * signal is aborted, nothing more is shown: each layer clears what it loads instead, and the
   * container's geodata is dropped, see ProjectGeodataManager.activateTimelineContainer.
   */
  private async showTimelineContainer(timelineContainerId: string, signal: AbortSignal) {
    if (ViewSettings.instance.isCadViewer) {
      // The CAD viewer shows no timeline data, but the chosen timeline is still the active one
      ProjectGeodataManager.instance().selectTimelineContainer(timelineContainerId);
      this.loadingTimelineContainerId = undefined;
      return;
    }

    // The camera keeps its place in the world if the origo moves
    cameraManager().updateGeographicCoords();
    const geodata = ProjectGeodataManager.instance();
    const available = geodata.resolveAvailableContainerGeodata(timelineContainerId);
    this.removeTimelineLayers(available.hasOrthoTile);
    // Their tiles and GPU buffers, which the next container's replace. Not when the scene is only
    // deactivated: back from another scene, it shows them again.
    PointCloudHandle.instance.clearCustom();
    Tiled3DModelHandle.instance.setActiveTiledModel(undefined);

    const activation = geodata.activateTimelineContainer(timelineContainerId).then(() => {
      signal.throwIfAborted();
      if (this.setOrigo(true)) {
        // Its tiles are placed from the last origo
        this.disposeOrthophotoOverview();
      }
      this.showAvailableLayers();
    });
    const statuses = LayerLoadingStatuses.instance;
    await Promise.all([
      available.hasOrthoTile &&
        statuses.track(
          'orthophoto',
          async () => {
            try {
              await activation;
              await this.addOrthophoto();
            } finally {
              // Also when it fails. Another timeline chosen meanwhile keeps it for its own.
              if (!signal.aborted) {
                this.disposeOrthophotoOverview();
              }
            }
          },
          signal,
        ),
      available.hasScan &&
        statuses.track(
          'tiledModel',
          async () => {
            await activation;
            await this.addTiledModel(await loadTiledModel(signal), signal);
          },
          signal,
        ),
      available.hasPointCloud &&
        statuses.track(
          'pointCloud',
          async () => {
            await activation;
            this.addPointCloud(await loadPointCloud(signal), signal);
          },
          signal,
        ),
      // The layers report its failure
      activation.catch((err) => {
        if (!signal.aborted) {
          console.error(err);
        }
      }),
    ]);
    if (signal.aborted) {
      return;
    }
    this.loadingTimelineContainerId = undefined;

    if (this.focusOnLoad) {
      this.focusOnLoad = false;
      // Shared views are saved in the classic view's own scene, which had its own origo
      if (!ViewSettings.instance.isFlexible) {
        const sceneType = toClassicSceneType(ViewSettings.instance.classicView);
        cameraManager().transformSharedViewCamera((transformation) =>
          fromClassicSceneTransformation(transformation, sceneType),
        );
      }
      cameraManager().focusOnScene();
    }
    this.publishBoundingBox();
  }

  deactivate() {
    console.log('Deactivated SkyView scene');
    this.active = false;
    this.returnController?.abort();
    // A container that hasn't loaded loads again once the scene is shown
    this.loadController?.abort();
    if (this.loadingTimelineContainerId) {
      this.pendingTimelineContainerId = this.loadingTimelineContainerId;
      this.loadingTimelineContainerId = undefined;
    }
    cameraManager().updateGeographicCoords();
    this.scaleHandler?.deactivate();

    this.unsubscribeViewSettings?.();
    this.unsubscribeViewSettings = undefined;
    if (this.onToolChangedSubscription) {
      unsubscribe(this.onToolChangedSubscription);
      this.onToolChangedSubscription = undefined;
    }

    this.removeTimelineLayers();
    this.clearChildren(this as unknown as THREE.Mesh);
  }

  private getProjection() {
    const projection = ProjectGeodataManager.instance().getProjectProjection();
    if (!projection) {
      throw new Error(`The project projection is not defined.`);
    }
    return projection;
  }

  /**
   * @param onlyIfMoved True to leave the projection data as it is when the origo stays: the tools
   * would otherwise move their points by the last change of the origo again.
   * @returns Whether the origo moved.
   */
  private setOrigo(onlyIfMoved = false) {
    const projection = this.getProjection();
    const coordinates = CoordinateManager.getInstance();
    const current = coordinates.getProjectionOrigo();
    const origo = resolveSkyviewSceneOrigo(
      ProjectGeodataManager.instance().getActiveTimelineContainer(),
      current,
    );
    const moved = !current?.equals(origo);
    if (!onlyIfMoved || moved) {
      coordinates.setProjectProjectionData(projection, origo);
    }
    return moved;
  }

  /** Shows the layers the timeline container has, by the viewing mode. */
  private showAvailableLayers() {
    const settings = ViewSettings.instance;
    if (settings.isFlexible) {
      const available = ProjectGeodataManager.instance().resolveAvailableContainerGeodata();
      settings.showDefaultLayer({
        orthophoto: available.hasOrthoTile,
        tiledModel: available.hasScan,
        pointCloud: available.hasPointCloud,
      });
    } else {
      settings.showClassicView(resolveClassicView(settings.classicView));
    }
  }

  /**
   * Activates and adds the timeline container's tiled model, see loadTiledModel, making it the
   * active one. If it fails to activate, or the signal is aborted, it's cleared instead, while it
   * is still this call's.
   *
   * @param ownsModel False once another call activates the same model, which it then leaves be.
   */
  private async addTiledModel(
    tiledModel: Tiled3dModel | undefined,
    signal?: AbortSignal,
    ownsModel = () => true,
  ) {
    if (!tiledModel) {
      return;
    }

    try {
      await tiledModel.activate();
      signal?.throwIfAborted();
    } catch (err) {
      if (ownsModel()) {
        tiledModel.clear();
        tiledModel.clearCustom();
      }
      throw err;
    }

    Tiled3DModelHandle.instance.setActiveTiledModel(tiledModel);
    this.add(tiledModel);
    this.hasTiledModel = true;
    this.applyLayerVisibility();
  }

  /**
   * Adds the timeline container's point cloud, see loadPointCloud, making it the active one. Once
   * the signal is aborted, it's cleared instead.
   */
  private addPointCloud(pointCloud: PointCloud | undefined, signal?: AbortSignal) {
    if (signal?.aborted) {
      pointCloud?.clearCustom();
      signal.throwIfAborted();
    }

    PointCloudHandle.instance.setActivePointCloud(pointCloud);
    if (!pointCloud) {
      return;
    }

    PointCloudHandle.instance.displayPointCloud = true;
    this.add(pointCloud);
    this.hasPointCloud = true;
    this.edlPass?.dispose();
    this.edlPass = new EdlPass(window.innerWidth, window.innerHeight);
    this.edlPass.setCameraPlanes(near, far);
    this.applyLayerVisibility();
  }

  /** Adds the active timeline container's orthophoto. Resolves once its root tiles have loaded. */
  private async addOrthophoto() {
    if (!ProjectGeodataManager.instance().getActivOrthotileGeodata()) {
      return;
    }

    const asset = createActiveOrthoTileAsset({
      id: 'skyview-scene',
      projection: this.getProjection(),
      sceneOriginProject: CoordinateManager.getInstance().getProjectionOrigo()!,
    });
    const rootsLoaded = asset.preloadRoots();
    this.add(asset);
    this.orthoTileAsset = asset;
    this.unbindOrthophotoSettings = bindOrthophotoSettings(asset);
    this.applyLayerVisibility();
    await rootsLoaded;
  }

  /**
   * @param keepOrthophotoOverview True to keep the orthophoto's overview for the next timeline's
   * orthophoto to load over, see keepOrthophotoOverview.
   */
  private removeTimelineLayers(keepOrthophotoOverview = false) {
    if (keepOrthophotoOverview) {
      this.keepOrthophotoOverview();
    } else {
      this.disposeOrthoTileAsset();
      this.disposeOrthophotoOverview();
    }

    if (this.hasTiledModel) {
      this.remove(Tiled3DModelHandle.instance.getActiveTiledModel()!);
      Tiled3DModelHandle.instance.clearCustom();
      this.hasTiledModel = false;
    }

    if (this.hasPointCloud) {
      const pointCloud = PointCloudHandle.instance.activePointCloud;
      if (pointCloud) {
        pointCloud.visible = true;
        this.remove(pointCloud);
      }
      this.hasPointCloud = false;
    }
    this.edlPass?.dispose();
    this.edlPass = undefined;
  }

  private publishBoundingBox() {
    // The tiled model publishes its own bounding box once it's loaded
    const tiledModel = Tiled3DModelHandle.instance.getActiveTiledModel();
    if (!(this.hasTiledModel && tiledModel instanceof Cesium3dTilesRenderer)) {
      publish(SubscriptionTopic.OnSceneBoundingBoxChanged, {
        boundingBox: this.getGeodataBounderies(),
      });
    }
  }

  isOrthographicCameraActive() {
    return cameraManager().getActiveCamera()?.cameraType === CameraType.OrthographicCamera;
  }

  /** Whether the orthophoto is shown, so not hidden or replaced by the comparison tool. */
  isOrthophotoVisible() {
    return !!this.orthoTileAsset?.visible;
  }

  /**
   * Whether the timeline's point cloud loads: when shown, and always in the classic viewing mode,
   * which picks from it in every view, as its scenes did.
   */
  private loadsPointCloud() {
    return (
      this.hasPointCloud &&
      (ViewSettings.instance.layers.pointCloud || !ViewSettings.instance.isFlexible)
    );
  }

  /** Whether the timeline's point cloud is shown. */
  isPointCloudVisible() {
    return this.hasPointCloud && ViewSettings.instance.layers.pointCloud;
  }

  /** Whether the timeline's tiled model is shown. */
  isTiledModelVisible() {
    return this.hasTiledModel && ViewSettings.instance.layers.tiledModel;
  }

  async animate() {
    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    const { layers } = ViewSettings.instance;
    if (this.hasTiledModel && layers.tiledModel) {
      await Tiled3DModelHandle.instance.loadLodChunks(camera);
    }
    if (this.loadsPointCloud()) {
      await PointCloudHandle.instance.loadLodChunks(camera);
    }

    AssetManager.instance.onAnimate(camera);
  }

  render(renderer: THREE.WebGLRenderer) {
    const skyviewCamera = cameraManager().getActiveCamera();
    if (!skyviewCamera) {
      throw new Error('Camera not defined.');
    }
    const camera = skyviewCamera.getTHREECamera();
    const { layers } = ViewSettings.instance;

    if (this.hasTiledModel && layers.tiledModel) {
      const tiledModel = Tiled3DModelHandle.instance.getActiveTiledModel();
      if (tiledModel instanceof Cesium3dTilesRenderer || tiledModel instanceof Tiles3tzModel) {
        tiledModel.setCameraAndResolution(camera, renderer);
        tiledModel.update();
      }
      Tiled3DModelHandle.instance.cullLodChunks(skyviewCamera);
    }

    if (this.hasPointCloud && layers.pointCloud) {
      const pointCloud = PointCloudHandle.instance.activePointCloud;
      if (
        pointCloud instanceof Cesium3dTilesPointCloudRenderer ||
        pointCloud instanceof CopcPointCloud
      ) {
        pointCloud.setCameraAndResolution(camera, renderer);
        pointCloud.update();
      } else {
        CesiumPointsMaterial.instance.screenHeight = renderer.domElement.height;
      }
    }
    if (this.loadsPointCloud()) {
      PointCloudHandle.instance.cullLodChunks(skyviewCamera);
    }

    if (this.isOrthographicCameraActive()) {
      WmsState.instance.render(renderer, camera);
    }
    applyLayerOpacity();
    // A hidden orthophoto loads no tiles
    if (this.orthoTileAsset?.visible) {
      this.orthoTileAsset.update(camera, renderer);
    }

    const drawScene = () =>
      SunManager.instance.renderObjectWithLightSources(renderer, this, camera);
    // Eye dome lighting reads a perspective depth buffer
    const useEdl =
      !!PointCloudHandle.instance.activePointCloud?.visible && !this.isOrthographicCameraActive();
    if (this.edlPass && useEdl) {
      // Sized to the drawing buffer every frame, see the point cloud scene
      this.edlPass.setSize(renderer.domElement.width, renderer.domElement.height);
      this.edlPass.render(renderer, drawScene);
    } else {
      drawScene();
    }
  }

  inactive() {
    /* noop */
  }

  clearData() {
    this.initialized = false;

    this.disposeOrthoTileAsset();
    this.disposeOrthophotoOverview();
    this.clearChildren(this as unknown as THREE.Mesh);

    for (const uuid of [this.perspectiveCameraUuid, this.orthographicCameraUuid]) {
      const camera = cameraManager().getCamera(uuid);
      if (camera) {
        this.remove(camera.getTHREECamera());
      }
    }
  }

  clearCustom() {
    this.loadController?.abort();
    this.clearData();
    cameraManager().destroyCamera(this.perspectiveCameraUuid);
    cameraManager().destroyCamera(this.orthographicCameraUuid);
    PointCloudHandle.instance.clearCustom();
    // Cleared once the scene was deactivated, see removeTimelineLayers
    Tiled3DModelHandle.instance.setActiveTiledModel(undefined);
    this.edlPass?.dispose();
    this.edlPass = undefined;
  }

  isLoading() {
    return (
      (this.hasTiledModel && Tiled3DModelHandle.instance.isLoading()) ||
      AssetManager.instance.isLoading()
    );
  }

  valid() {
    return true;
  }

  /**
   * The bounds of the shown layers, first the orthophoto's, then the tiled model's, then the point
   * cloud's, otherwise of the hidden ones in that order. Bounds that are still empty are skipped:
   * the tiled model and point cloud only have bounds once loaded, and an unchecked layer doesn't
   * load, while the scene focuses on these bounds when it activates.
   */
  getGeodataBounderies(): THREE.Box3 {
    const boundsByLayer: [ViewLayer, (() => THREE.Box3) | undefined][] = [
      [
        'orthophoto',
        this.orthoTileAsset
          ? () =>
              getOrthotileBounds(
                CoordinateManager.getInstance().getProjectProjection(),
                CoordinateManager.getInstance().getProjectionOrigo() ?? new THREE.Vector3(),
              )
          : undefined,
      ],
      [
        'tiledModel',
        this.hasTiledModel ? () => Tiled3DModelHandle.instance.getBoundingBox() : undefined,
      ],
      [
        'pointCloud',
        this.hasPointCloud ? () => PointCloudHandle.instance.getBoundingBox() : undefined,
      ],
    ];
    const { layers } = ViewSettings.instance;
    const ordered = [
      ...boundsByLayer.filter(([layer]) => layers[layer]),
      ...boundsByLayer.filter(([layer]) => !layers[layer]),
    ];

    for (const [, getBounds] of ordered) {
      const box = getBounds?.();
      if (box && !box.isEmpty()) {
        return box;
      }
    }
    return new THREE.Box3();
  }

  getWgs84Boundries() {
    return this.orthoTileAsset ? getOrthotileWgs84Bounds() : undefined;
  }

  /**
   * @param withinScene True when switching camera in this scene, so the origo stays the same.
   */
  private activateCamera(type: FlexibleCameraType, withinScene: boolean) {
    const uuid = type === 'perspective' ? this.perspectiveCameraUuid : this.orthographicCameraUuid;
    const { cameraControl } = ViewSettings.instance;
    if (withinScene && cameraManager().getActiveCamera()?.uuid === uuid) {
      // The camera stays, but how it is controlled may change
      if (type === 'perspective' && cameraManager().getCameraControl() !== cameraControl) {
        cameraManager().setPerspectiveController(cameraControl);
      }
      return;
    }

    if (withinScene) {
      // Updates the focus point the new camera copies its view from
      cameraManager().updateGeographicCoords();
    }

    const camera = cameraManager().setActiveCamera(uuid, withinScene);
    if (!camera) {
      throw new Error(`The SkyView scene camera does not exist: ${uuid}`);
    }
    camera.addToScene(this);

    if (type === 'perspective') {
      cameraManager().setPerspectiveController(cameraControl);
    } else {
      cameraManager().setOrthotileController();
    }
  }

  private disposeOrthoTileAsset() {
    this.unbindOrthophotoSettings?.();
    this.unbindOrthophotoSettings = undefined;
    if (this.orthoTileAsset) {
      this.disposeOrthophoto(this.orthoTileAsset);
      this.orthoTileAsset = undefined;
    }
  }

  private disposeOrthophotoOverview() {
    if (this.orthophotoOverview) {
      this.disposeOrthophoto(this.orthophotoOverview);
      this.orthophotoOverview = undefined;
    }
  }

  private disposeOrthophoto(asset: OrthoTileAsset) {
    this.remove(asset);
    // Cancels tile requests and frees cached textures that aren't on a tile mesh
    asset.dispose();
    disposeRecursive(asset);
  }

  /**
   * Keeps the shown orthophoto's overview, its root tiles, under the next timeline's orthophoto
   * until that one has loaded its own, so the view doesn't go empty in between. An orthophoto
   * replaced before then is dropped instead, and the overview it replaced stays.
   */
  private keepOrthophotoOverview() {
    if (this.orthophotoOverview) {
      this.disposeOrthoTileAsset();
      return;
    }

    const asset = this.orthoTileAsset;
    if (!asset) {
      return;
    }
    this.unbindOrthophotoSettings?.();
    this.unbindOrthophotoSettings = undefined;
    this.orthoTileAsset = undefined;
    asset.showRootsOnly(renderOrders.orthoPhotoOverview);
    this.orthophotoOverview = asset;
  }

  private applyLayerVisibility() {
    const { layers, orthophoto } = ViewSettings.instance;
    const orthographic = this.isOrthographicCameraActive();

    const orthophotoVisible =
      layers.orthophoto && !InputHandler.instance.isOrthophotoComparisonToolActive();
    if (this.orthoTileAsset?.visible && !orthophotoVisible) {
      // Not updated while hidden, see render
      this.orthoTileAsset.cancelLoading();
    }
    for (const asset of [this.orthoTileAsset, this.orthophotoOverview]) {
      if (!asset) {
        continue;
      }
      asset.visible = orthophotoVisible;
      // The orthographic camera looks straight down, where the orthophoto's height doesn't show
      asset.position.z = orthographic
        ? 0
        : orthophoto.altitude - (CoordinateManager.getInstance().getProjectionOrigo()?.z ?? 0);
      asset.updateMatrixWorld(true);
    }

    // Every checked layer is drawn; where they overlap, the nearest one shows, unless made
    // translucent with its opacity
    const tiledModel = Tiled3DModelHandle.instance.getActiveTiledModel();
    if (this.hasTiledModel && tiledModel) {
      tiledModel.visible = layers.tiledModel;
    }

    const pointCloud = PointCloudHandle.instance.activePointCloud;
    if (this.hasPointCloud && pointCloud) {
      pointCloud.visible = layers.pointCloud;
    }
  }
}
